import { PaginationV2, Pagination } from "@/domain/entities/Pagination";
import { AxiosResponse } from "axios";
import {
  HistoryHanging,
  ListRiwayatHangingLasmile
} from "@/domain/entities/HangingIncoming";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import {
  CardHangingShared,
  FollowUp,
  Group,
  GroupSummary,
  Stt
} from "@/domain/entities/CardHangingShared";
import {
  HistoryHangingBookingEntities,
  HistoryHangingBooking,
  HistoryFollowUpBookingEntities,
  HistoryFollowUpBooking,
  ActivitiesFollowUpBooking,
  FollowUpBooking
} from "@/domain/entities/HangingBooking";
import {
  HangingBookingPosSummary,
  HangingBookingPosSummaryDetail,
  HangingOutgoingSummary
} from "@/domain/entities/HangingOutgoing";
import { HangingList } from "@/domain/entities/Hanging";
import { HangingBkdSummaryConsole } from '@/domain/entities/HangingBkd';

export class HangingMapper {
  public convertHistoryHangingFromApi(res: AxiosResponse<any>): HistoryHanging {
    const {
      data: { data, meta }
    } = res;

    return new HistoryHanging({
      pagination: new PaginationV2({
        ...convertSnakeToCamel(meta),
        totalData: meta.total_records
      }),
      data:
        data?.map(
          (item: any) =>
            new ListRiwayatHangingLasmile(convertSnakeToCamel(item))
        ) || []
    });
  }

  convertHangingOutgoingSummaryFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;

    const byLabel = data?.by_label || [];

    return new HangingOutgoingSummary({
      lastSync: data.last_sync,
      totalActivities: data.activities.total,
      pendingActitivies: data.activities.pending,
      doneActivities: data.activities.done,

      totalSTI: byLabel.find((label: any) => label.label === "STI").total,
      pendingSTI: byLabel.find((label: any) => label.label === "STI").pending,
      doneSTI: byLabel.find((label: any) => label.label === "STI").done,

      totalBAGGING: byLabel.find((label: any) => label.label === "BAGGING").total,
      pendingBAGGING: byLabel.find((label: any) => label.label === "BAGGING").pending,
      doneBAGGING: byLabel.find((label: any) => label.label === "BAGGING").done,

      totalSTT: byLabel.find((label: any) => label.label === "STT").total,
      pendingSTT: byLabel.find((label: any) => label.label === "STT").pending,
      doneSTT: byLabel.find((label: any) => label.label === "STT").done,

      totalUserOps: byLabel.find((label: any) => label.label === "User Ops").total,
      pendingUserOps: byLabel.find((label: any) => label.label === "User Ops").pending,
      doneUserOps: byLabel.find((label: any) => label.label === "User Ops").done,
    });
  }

  convertHangingBookingPosSummaryFromApi(
    res: AxiosResponse<any>
  ): HangingBookingPosSummary {
    const {
      data: { data }
    } = res;

    return new HangingBookingPosSummary(convertSnakeToCamel(data));
  }

  convertHangingOutgoingFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;

    const dataList = data || [];

    return dataList.map((item: any) => {
      const groups = item.groups || [];

      return new CardHangingShared({
        activityId: item.activity_id,
        activityType: item.activity_type,
        followUpStatus: item.follow_up_status,
        followUp: new FollowUp({
          opsUsername: item?.follow_up?.ops_username,
          partnerType: item?.follow_up?.partner_type,
          partnerName: item?.follow_up?.partner_name,
          partnerCode: item?.follow_up?.partner_code,
          username: item?.follow_up?.username,
        }),
        followUpByUsername: item.follow_up_by_username,
        followUpAt: item.follow_up_at,
        groups: groups.map((g: any) => {
          const stts = g.stts || [];
          return new Group({
            groupName: g.group_name,
            summary: new GroupSummary({
              totalStt: g.summary?.total_stt,
              totalManifest: g.summary?.total_menifest || 0
            }),
            isChecked: false,
            stts: stts.map((stt: any) => {
              return new Stt({
                noManifest: stt.no_manifest,
                no: stt.no,
                bagNo: stt.bag_no,
                sttNo: stt.stt_no,
                isAggregator: stt.is_aggregator,
                productType: stt.product_type,
                hangingDays: stt.hanging_days,
                destination: stt.destination,
                existenceStatus: stt.existence_status,
                existenceLocation: stt.existence_location,
                existenceLocationNote: stt.existence_location_note,
                existenceFindAt: stt.existence_find_at,
                existenceUsername: stt.existence_username,
                isChecked: false
              });
            })
          });
        })
      });
    });
  }

  public convertHistoryHangingBookingListFromApi(
    res: AxiosResponse<any>
  ): HistoryHangingBookingEntities {
    const { data } = res;
    return new HistoryHangingBookingEntities({
      pagination: new Pagination(
        data.meta.page,
        data.meta.limit,
        data.meta.total_records
      ),
      data: data.data.length
        ? data.data.map(
            (item: any) =>
              new HistoryHangingBooking({
                manifestNo: item.manifest_no,
                sttNo: item.stt_no,
                aggregator: item.aggregator,
                hangingDays: item.hanging_days,
                existenceStatus: item.existence_status,
                existenceLocation: item.existence_location,
                existenceUsername: item.existence_username,
                existenceFindAt: item.existence_find_at,
                status: item.status,
                username: item.username,
                createdAt: item.created_at
              })
          )
        : []
    });
  }

  public convertHistoryFollowUpBookingFromApi(
    res: AxiosResponse<any>
  ): HistoryFollowUpBookingEntities {
    const { data } = res;

    return new HistoryFollowUpBookingEntities({
      pagination: new Pagination(
        data.meta.page,
        data.meta.limit,
        data.meta.total_records
      ),
      data: (data.data || []).map(
        (item: any) =>
          new HistoryFollowUpBooking({
            date: item.date,
            activities: (item.activities || []).map(
              (activity: any) =>
                new ActivitiesFollowUpBooking({
                  activityId: activity.activity_id,
                  activityType: activity.activity_type,
                  statusFollowUp: activity.status_follow_up,
                  followUp: new FollowUpBooking({
                    username: activity.follow_up?.username || "",
                    partnerType: activity.follow_up?.partner_type || "",
                    partnerId: activity.follow_up?.partner_id || 0,
                    partnerName: activity.follow_up?.partner_name || ""
                  }),
                  username: activity.username,
                  createdAt: activity.created_at
                })
            )
          })
      )
    });
  }

  public convertHangingListFromApi(res: AxiosResponse<any>): HangingList[] {
    const {
      data: { data }
    } = res;

    return (
      data?.map((item: any) => new HangingList(convertSnakeToCamel(item))) || []
    );
  }

  public convertHangingBkdConsoleSummaryFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;

    const byLabel = data?.by_label || [];

    return new HangingBkdSummaryConsole({
      lastSync: data.last_sync,
      totalActivities: data.activities.total,
      pendingActivities: data.activities.pending,
      doneActivities: data.activities.done,
      doneSttPos: byLabel.find((label: any) => label.label === "POS").done,
      totalSttPos: byLabel.find((label: any) => label.label === "POS").total,
      pendingSttPos: byLabel.find((label: any) => label.label === "POS").pending,
      byStatusPos: byLabel.find((label: any) => label.label === "POS").by_status,
      doneSttKorporat: byLabel.find((label: any) => label.label === "KORPORATE").done,
      totalSttKorporat: byLabel.find((label: any) => label.label === "KORPORATE").total,
      pendingSttKorporat: byLabel.find((label: any) => label.label === "KORPORATE").pending,
      byStatusKorporat: byLabel.find((label: any) => label.label === "KORPORATE").by_status,
      doneSttSubKon: byLabel.find((label: any) => label.label === "SUB-KON").done,
      pendingSttSubKon: byLabel.find((label: any) => label.label === "SUB-KON").pending,
      byStatusSubKon: byLabel.find((label: any) => label.label === "SUB-KON").by_status,
      totalSttSubKon: byLabel.find((label: any) => label.label === "SUB-KON").total,
    })
  }

  convertHangingBkdConsoleFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;

    const dataList = data || [];

    return dataList.map((item: any) => {
      const groups = item.groups || [];

      return new CardHangingShared({
        activityId: item.activity_id,
        activityType: item.activity_type,
        followUpStatus: item.follow_up_status,
        followUp: new FollowUp({
          partnerType: item?.follow_up?.partner_type,
          partnerName: item?.follow_up?.partner_name,
          partnerCode: item?.follow_up?.partner_code,
          username: item?.follow_up?.username,
        }),
        followUpByUsername: item.follow_up_by_username,
        followUpAt: item.follow_up_at,
        groups: groups.map((g: any) => {
          const stts = g.stts || [];
          return new Group({
            groupName: g.group_name,
            summary: new GroupSummary({
              totalStt: g.summary?.total_stt || 0,
              totalManifest: g.summary?.total_manifest || 0
            }),
            isChecked: false,
            stts: stts.map((stt: any) => {
              return new Stt({
                noManifest: stt.no_manifest,
                no: stt.no,
                bagNo: stt.bag_no,
                sttNo: stt.stt_no,
                isAggregator: stt.is_aggregator,
                productType: stt.product_type,
                hangingDays: stt.hanging_days,
                destination: stt.destination,
                existenceStatus: stt.existence_status,
                existenceLocation: stt.existence_location,
                existenceLocationNote: stt.existence_location_note,
                existenceFindAt: stt.existence_find_at,
                existenceUsername: stt.existence_username,
                isChecked: false
              });
            })
          });
        })
      });
    });
  }
}
